/**
 * Sort bridge for a DataTable that owns its sort locally.
 *
 * The app encodes sort as a STRING (`"columnId,dir[,nullHandling]"`) everywhere — URL state, the
 * filter chrome, the `onSortChange` callbacks — while the table engine uses TanStack's
 * `SortingState` + `OnChangeFn`. `ListTableWithFilters` bridges the two for
 * tables that go through the chrome; this hook is the same bridge for the embedded tables that keep
 * the sort string in their own state (and sort their rows in memory from it).
 */
import type { OnChangeFn, SortingState } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { convertSortToString, parseSortString } from '../list-table/sortUtil';

export const useDataTableSortString = (
    sort: string,
    onSortChange: (sort: string) => void,
    columns: object[]
): { sorting: SortingState; onSortingChange: OnChangeFn<SortingState> } => {
    const sorting = useMemo<SortingState>(
        () => (parseSortString(sort, columns) ?? []) as SortingState,
        [sort, columns]
    );
    const onSortingChange = useCallback<OnChangeFn<SortingState>>(
        (updater) =>
            onSortChange(convertSortToString(typeof updater === 'function' ? updater(sorting) : updater, columns)),
        [sorting, columns, onSortChange]
    );
    return { sorting, onSortingChange };
};

export default useDataTableSortString;
